import { t } from 'i18next';
import { Color } from 'three';

import { isDefined } from '../../../../../../../utils/variables';
import { LineSegmentNode } from '../../../../../../elements/design/design-node/utils/line-segment-node';
import {
  findPointIdFromIntersection,
  PointId,
} from '../../../../../../elements/design/design-node/utils/point-node';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import { ActionInProgress, ActionName, DesignTool } from '../../../../../design-tool';
import { PickingLine } from '../../../../../picking-line';
import { PickingSphere } from '../../../../../picking-sphere';
import { findShortestPathBetweenPointIdsDijkstra } from '../../utils/find-shortest-path-between-points';
import { getPointIdArray } from '../../utils/select';
import { initActionInProgress } from '../general';

type PolylinePoint = { id: PointId; sphere: PickingSphere };
export type PolylinePath = {
  startPoint: PointId;
  nodes: LineSegmentNode[];
  line: PickingLine;
};
type PredictedPath = {
  path: LineSegmentNode[] | undefined;
  toPointId: PointId | undefined;
  line: PickingLine;
};

const gray = new Color(0.5, 0.5, 0.5);
const red = new Color(1, 0, 0);
const orange = new Color(1, 0.5, 0);

/**
 * Adds a new point to the selection and returns true if successful, otherwise false.
 */
function addPoint(
  tool: DesignTool,
  points: PolylinePoint[],
  paths: PolylinePath[],
  predictedPath: PredictedPath,
  limitSelectionToPolyline: boolean,
  addPickers = true,
) {
  const id = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);

  if (!isDefined(id)) {
    return false;
  }

  if (points.length > 0) {
    // Don't allow adding points that are already added when limiting the selection to polyline.
    if (points.some((x) => x.id === id) && limitSelectionToPolyline) {
      return false;
    }

    if (!isDefined(predictedPath.path)) {
      return false;
    }

    predictedPath.line.setColor(red);

    paths.push({
      startPoint: points[0].id,
      nodes: predictedPath.path.slice(),
      line: predictedPath.line,
    });

    const position = predictedPath.line.position;
    predictedPath.line = new PickingLine(gray);
    predictedPath.line.position.copy(position);

    if (addPickers) {
      tool.add(predictedPath.line);
    }
  }

  const sphere = new PickingSphere(red);
  points.push({ id, sphere });

  const newPos = tool.asset.designNode.getVertexByPointId(id, 'world');
  sphere.position.copy(newPos);

  if (addPickers) {
    tool.add(sphere);
  }

  return true;
}

/**
 * Calculate the path between last point and currently hovered point.
 */
function refreshPathPreview(
  tool: DesignTool,
  points: PolylinePoint[],
  paths: PolylinePath[],
  predictedPath: PredictedPath,
  limitSelectionToPolyline: boolean,
) {
  if (!isDefined(tool.currentPoint) || points.length === 0) {
    predictedPath.path = undefined;
    predictedPath.toPointId = undefined;
    predictedPath.line.position.set(0, 0, 0);
    predictedPath.line.setNonIndexedVertices([]);
    predictedPath.line.setColor(gray);
    return;
  }

  const nextId = findPointIdFromIntersection(tool.asset.designNode, tool.intersection);

  // Only refresh the path if picked point id has changed or if no point id is found.
  const refreshPath = predictedPath.toPointId !== nextId || !isDefined(nextId);
  const excludedPoints = limitSelectionToPolyline
    ? getPointIdArray(paths.flatMap((x) => x.nodes))
    : [];

  const path =
    isDefined(nextId) && refreshPath
      ? findShortestPathBetweenPointIdsDijkstra(
          tool.asset.designNode,
          points.at(-1)!.id,
          nextId,
          excludedPoints,
        )
      : undefined;

  predictedPath.toPointId = nextId;

  if (isDefined(path)) {
    predictedPath.path = path;
    predictedPath.line.position.set(0, 0, 0);
    predictedPath.line.setNonIndexedVertices(
      path.flatMap((segment) => {
        return segment.values.map((pointId) =>
          tool.asset.designNode.getVertexByPointId(pointId, 'world'),
        );
      }),
    );
    predictedPath.line.setColor(orange);
  } else if (refreshPath) {
    predictedPath.path = path;
    predictedPath.line.position.set(0, 0, 0);
    predictedPath.line.setNonIndexedVertices([points.at(-1)!.sphere.position, tool.currentPoint]);
    predictedPath.line.setColor(gray);
  }
}

const initAction = (
  tool: DesignTool,
  actionName: ActionName,
  apply: (paths: PolylinePath[]) => void,
  limitSelectionToPolyline: boolean,
): void => {
  const points: PolylinePoint[] = [];
  const paths: PolylinePath[] = [];
  const predictedPath: PredictedPath = {
    path: undefined,
    line: new PickingLine(gray),
    toPointId: undefined,
  };

  const onMouseUp = (activeAction: ActionInProgress) => {
    const pointAdded = addPoint(tool, points, paths, predictedPath, limitSelectionToPolyline);

    // If this is the first point, set it as the start point.
    if (pointAdded && points.length === 1) {
      activeAction.originPointId = points[0].id;
    }

    publish(SubscriptionTopic.DesignToolActiveActionChanged);
  };

  addPoint(tool, points, paths, predictedPath, limitSelectionToPolyline);

  initActionInProgress(tool, {
    actionName,
    originPointId: points.length > 0 ? points[0].id : undefined,
    initiate: () => {
      addPoint(tool, points, paths, predictedPath, limitSelectionToPolyline);
      tool.add(predictedPath.line);
    },
    onMouseUp: () => {
      if (tool.actionInProgress) {
        onMouseUp(tool.actionInProgress);
      }
    },
    cleanup: () => {
      points.forEach((p) => tool.remove(p.sphere));
      paths.forEach((p) => tool.remove(p.line));
      tool.remove(predictedPath.line);
    },
    getExtraOptions: () => {
      return [
        {
          title: t('section.functions.interpolatePointHeights.action.addPoint', {
            ns: 'skyviewDesign',
          }),
          action: () => {
            if (tool.actionInProgress) {
              onMouseUp(tool.actionInProgress);
            }
            tool.contextMenuCallback?.();
          },
          disabled: !isDefined(
            findPointIdFromIntersection(tool.asset.designNode, tool.intersection),
          ),
        },
        {
          title: t('section.functions.interpolatePointHeights.action.removeLastPoint', {
            ns: 'skyviewDesign',
          }),
          action: () => {
            if (paths.length > 0 && points.length > 0) {
              const lastPoint = points.pop()!;
              const lastPath = paths.pop()!;
              tool.remove(lastPoint.sphere);
              tool.remove(lastPath.line);
              tool.contextMenuCallback?.();
              publish(SubscriptionTopic.DesignToolActiveActionChanged);
            }
          },
          disabled: points.length < 2,
        },
      ];
    },
    refresh: () => {
      refreshPathPreview(tool, points, paths, predictedPath, limitSelectionToPolyline);
    },
    onProjectProjectionUpdated: (diff) => {
      points.forEach((p) => p.sphere.position.add(diff));
      paths.forEach((p) => p.line.position.add(diff));
      predictedPath.line.position.add(diff);
    },
    readyToApply: () => points.length > 1 && paths.length === points.length - 1,
    apply: () => {
      addPoint(tool, points, paths, predictedPath, limitSelectionToPolyline, false);
      apply(paths);
    },
  });
};

export const selectPolylineUtils = {
  initAction,
};
